import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Collapse } from "react-bootstrap";
import Select from "react-select";
import { useSearchParams } from "react-router-dom";
import { MdKeyboardArrowDown, MdEdit, MdDeleteOutline, MdSecurity, MdOutlineCheckCircle, MdOutlineRemoveCircleOutline, MdAddCircle, MdFileDownload } from "react-icons/md";
import { FiRefreshCw } from "react-icons/fi";
import { BsThreeDotsVertical } from "react-icons/bs";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  Utils,
  fetchWithAuth,
  selectStyles,
  handleSessionExpired,
  getDefaultPageSize,
  DEFAULT_STATUS_OPTIONS,
  formatDateTime,
  groupAdminPermissionsForUi,
  TEAM_MEMBER_ROLE_SLUG,
  getCustomerSupportPermissionKeys,
  getStatusDisplay,
  can,
  exportListCsv,
} from "../../utils";
import { useActionDropdown } from "../../hooks/useActionDropdown";
import { useTableMaxHeight } from "../../hooks/useTableMaxHeight";
import { useScrollEndDetection } from "../../hooks/useScrollEndDetection";
import ListPaginationBar from "../common/ListPaginationBar";

const emptyForm = { id: null, name: "", status: 1, permission_ids: [], slug: "" };

const Roles = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageSize = getDefaultPageSize();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ name: "", status: "" });
  const [draftFilters, setDraftFilters] = useState({ name: "", status: "" });
  const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // create | name | permissions
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [openMenus, setOpenMenus] = useState({});

  const tableScrollRef = useRef(null);
  const tableInnerRef = useRef(null);
  const tableMaxHeight = useTableMaxHeight(tableScrollRef);
  const isAtScrollEnd = useScrollEndDetection(tableScrollRef, tableInnerRef, rows.length);
  const {
    openActionId,
    openActionItem,
    dropdownPosition,
    dropdownRef,
    handleDropdownAction,
    handleOpenDropdown,
  } = useActionDropdown({
    tableScrollRef,
    optionCount: (item) =>
      (can("roles.edit") ? 2 : 0) +
      (can("roles.change_status") ? 1 : 0) +
      (!item?.is_system && can("roles.delete") ? 1 : 0),
  });

  const permissionsTree = useMemo(
    () => groupAdminPermissionsForUi(permissions),
    [permissions]
  );

  const lockedSupportKeys = useMemo(() => getCustomerSupportPermissionKeys(), []);
  const lockedSupportIds = useMemo(
    () => permissions.filter((p) => lockedSupportKeys.includes(p.key)).map((p) => p.id),
    [permissions, lockedSupportKeys]
  );
  const isSupportTeamRole = form.slug === TEAM_MEMBER_ROLE_SLUG;

  const ensureLockedPermissions = (ids) => {
    if (!isSupportTeamRole || !lockedSupportIds.length) return ids;
    return [...new Set([...(ids || []), ...lockedSupportIds])];
  };

  const toggleMenuOpen = (section, menu) => {
    const key = `${section}::${menu}`;
    setOpenMenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isMenuOpen = (section, menu) => Boolean(openMenus[`${section}::${menu}`]);

  const selectAllPermissions = () => {
    const allIds = permissions.map((p) => p.id);
    const allSelected = allIds.length > 0 && allIds.every((id) => form.permission_ids.includes(id));
    setForm((prev) => ({
      ...prev,
      permission_ids: allSelected
        ? ensureLockedPermissions([])
        : allIds,
    }));
  };

  const loadPermissions = async () => {
    const res = await fetchWithAuth(`${Utils.baseurl}user/permissions`);
    const result = await res.json().catch(() => ({}));
    if (handleSessionExpired(res, result)) return;
    if (result.status) setPermissions(result.data || []);
  };

  const loadData = async (nextPage = page, nextFilters = filters) => {
    setLoading(true);
    const res = await fetchWithAuth(`${Utils.baseurl}user/roles`, {
      method: "POST",
      body: JSON.stringify({
        page: nextPage,
        pageSize,
        name: (nextFilters.name || "").trim(),
        status: nextFilters.status,
      }),
    });
    const result = await res.json().catch(() => ({}));
    setLoading(false);
    if (handleSessionExpired(res, result)) return;
    if (!result.status) return toast.error(result.message || "Failed to load roles");
    setRows(result.data || []);
    setMeta(result.meta || { page: 1, totalPages: 1, total: 0 });
  };

  useEffect(() => {
    loadPermissions();
  }, []);

  useEffect(() => {
    const p = Math.max(1, parseInt(searchParams.get("page") || "1", 10));
    const name = searchParams.get("name") || "";
    const status = searchParams.get("status") || "";
    const f = { name, status };
    setPage(p);
    setFilters(f);
    setDraftFilters(f);
    setFilterAccordionOpen(Boolean(name || status));
    loadData(p, f);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const writeParams = (p, f) => {
    const sp = new URLSearchParams();
    if (p > 1) sp.set("page", String(p));
    if ((f.name || "").trim()) sp.set("name", f.name.trim());
    if (String(f.status || "").trim() !== "") sp.set("status", String(f.status).trim());
    setSearchParams(sp, { replace: true });
  };

  const onApplyFilters = () => {
    const f = { ...draftFilters };
    setFilters(f);
    setPage(1);
    setFilterAccordionOpen(Boolean((f.name || "").trim() || String(f.status || "").trim() !== ""));
    writeParams(1, f);
  };

  const onResetFilters = () => {
    const f = { name: "", status: "" };
    setDraftFilters(f);
    setFilters(f);
    setPage(1);
    setFilterAccordionOpen(false);
    writeParams(1, f);
  };

  const openCreate = () => {
    setModalMode("create");
    setForm(emptyForm);
    setFormErrors({});
    setShowModal(true);
  };

  const openEditName = (role) => {
    setModalMode("name");
    setForm({
      id: role.id,
      name: role.name || "",
      status: role.status ?? 1,
      permission_ids: role.permission_ids || [],
      slug: role.slug || "",
    });
    setFormErrors({});
    setShowModal(true);
  };

  const openEditPermissions = (role) => {
    setModalMode("permissions");
    const isLockedRole = role.slug === TEAM_MEMBER_ROLE_SLUG;
    const baseIds = role.permission_ids || [];
    const permission_ids = isLockedRole
      ? [...new Set([...baseIds, ...lockedSupportIds])]
      : baseIds;
    setForm({
      id: role.id,
      name: role.name || "",
      status: role.status ?? 1,
      permission_ids,
      slug: role.slug || "",
    });
    setFormErrors({});
    setShowModal(true);
  };

  const togglePermission = (id) => {
    if (isSupportTeamRole && lockedSupportIds.includes(id)) return;
    setForm((prev) => {
      const set = new Set(prev.permission_ids);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...prev, permission_ids: [...set] };
    });
  };

  const toggleModule = (modulePerms) => {
    const ids = modulePerms.map((p) => p.id);
    const lockedInModule = isSupportTeamRole
      ? ids.filter((id) => lockedSupportIds.includes(id))
      : [];
    const toggleable = ids.filter((id) => !lockedInModule.includes(id));
    if (!toggleable.length && lockedInModule.length) return;
    const allSelected = toggleable.length > 0 && toggleable.every((id) => form.permission_ids.includes(id));
    setForm((prev) => {
      const set = new Set(prev.permission_ids);
      lockedInModule.forEach((id) => set.add(id));
      if (allSelected) toggleable.forEach((id) => set.delete(id));
      else toggleable.forEach((id) => set.add(id));
      return { ...prev, permission_ids: [...set] };
    });
  };

  const save = async () => {
    const errors = {};
    if (modalMode !== "permissions" && !(form.name || "").trim()) {
      errors.name = "Role name is required";
    }
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }
    setSaving(true);
    try {
      let body;
      if (modalMode === "permissions") {
        body = {
          id: form.id,
          mode: "permissions",
          permission_ids: ensureLockedPermissions(form.permission_ids),
        };
      } else if (modalMode === "name") {
        body = { id: form.id, mode: "name", name: form.name.trim() };
      } else {
        body = {
          name: form.name.trim(),
          status: 1,
          permission_ids: form.permission_ids,
        };
      }
      const res = await fetchWithAuth(`${Utils.baseurl}user/roles/save`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      const result = await res.json().catch(() => ({}));
      if (handleSessionExpired(res, result)) return;
      if (!result.status) {
        toast.error(result.message || "Save failed");
        return;
      }
      toast.success(result.message || "Saved");
      setShowModal(false);
      loadData(page, filters);
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (role, nextStatus) => {
    const res = await fetchWithAuth(`${Utils.baseurl}user/roles/status`, {
      method: "POST",
      body: JSON.stringify({ id: role.id, status: nextStatus }),
    });
    const result = await res.json().catch(() => ({}));
    if (handleSessionExpired(res, result)) return;
    if (!result.status) return toast.error(result.message || "Status update failed");
    toast.success(result.message || "Status updated");
    loadData(page, filters);
  };

  const handleDelete = async (role) => {
    if (role.is_system) {
      toast.info("Fixed roles cannot be deleted");
      return;
    }
    const confirm = await Swal.fire({
      title: "Delete role?",
      text: `"${role.name}" will be permanently deleted.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Delete",
    });
    if (!confirm.isConfirmed) return;
    const res = await fetchWithAuth(`${Utils.baseurl}user/roles/delete`, {
      method: "POST",
      body: JSON.stringify({ id: role.id }),
    });
    const result = await res.json().catch(() => ({}));
    if (handleSessionExpired(res, result)) return;
    if (!result.status) return toast.error(result.message || "Delete failed");
    toast.success(result.message || "Deleted");
    loadData(page, filters);
  };

  const modalTitle =
    modalMode === "create" ? "Create Role" : modalMode === "permissions" ? "Change Permissions" : "Edit Role";

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const { status, message } = await exportListCsv("user/roles", {
        name: (filters.name || "").trim(),
        status: filters.status,
      }, "roles.csv");
      if (!status) {
        toast.error(message);
        return;
      }
      if (message) toast.info(message);
    } catch (error) {
      console.error(error);
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="dashboard_right">
      <div className="dashboard_inner">
        <div className="dashboard_right_top">
          <div className="dashboard_heading">
            <h1>Roles and Permissions</h1>
            <p className="text-muted mb-0" style={{ fontSize: 13 }}>
              Manage roles and permissions. System roles cannot be deleted.
            </p>
          </div>
          <div className="add_app" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Refresh list" onClick={() => loadData()}>
              <FiRefreshCw />
              <span>Refresh</span>
            </button>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Export CSV for applied filters" onClick={handleExport} disabled={isExporting}>
              <MdFileDownload />
              <span>{isExporting ? "Exporting…" : "Export"}</span>
            </button>
            {can("roles.add") ? (
              <button type="button" className="add_app_btn btn_theme_primary" onClick={openCreate}>
                <MdAddCircle />
                <span>Create Role</span>
              </button>
            ) : null}
          </div>
        </div>

        <div className="list-page-filter-card">
          <button
            type="button"
            className="list-page-filter-accordion-header"
            onClick={() => setFilterAccordionOpen((v) => !v)}
            aria-expanded={filterAccordionOpen}
          >
            <span className="list-page-filter-accordion-title">Filter</span>
            <MdKeyboardArrowDown className={`list-page-filter-accordion-chevron ${filterAccordionOpen ? "list-page-filter-accordion-chevron--open" : ""}`} size={22} />
          </button>
          <Collapse in={filterAccordionOpen}>
            <div className="list-page-filter-accordion-body">
              <div className="list-page-filter-bar">
                <div className="list-page-filter-fields row g-3 align-items-end">
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <input
                      className="form-control"
                      placeholder="Role name"
                      value={draftFilters.name}
                      onChange={(e) => setDraftFilters((p) => ({ ...p, name: e.target.value }))}
                    />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <Select
                      inputId="roles-filter-status"
                      instanceId="roles-filter-status"
                      classNamePrefix="ns-roles-filter-status"
                      isSearchable
                      isClearable
                      placeholder="All Status"
                      options={DEFAULT_STATUS_OPTIONS}
                      value={DEFAULT_STATUS_OPTIONS.find((o) => String(o.value) === String(draftFilters.status)) || null}
                      onChange={(opt) => setDraftFilters((p) => ({ ...p, status: opt?.value ?? "" }))}
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                  </div>
                  <div className="list-page-filter-field list-page-filter-actions col-12">
                    <div className="add_app">
                      <button type="button" className="add_app_btn btn_theme_primary" onClick={onApplyFilters}><span>Search</span></button>
                      <button type="button" className="add_app_btn_secondary btn_theme_secondary" onClick={onResetFilters}><span>Reset</span></button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Collapse>
        </div>

        <div className="dashboard_app_table dashboard_app_table--list-records">
          <div className="dashboard_table">
            <div ref={tableScrollRef} className="table_scroll_wrapper">
              <div
                ref={tableInnerRef}
                className={`table_inner table_y_scroll ${isAtScrollEnd ? "at-scroll-end" : ""}`}
                style={{
                  maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : "calc(100vh - 320px)",
                  overflowY: "auto",
                  overflowX: "auto",
                }}
              >
                <table className="table_outer table-sm">
                  <thead>
                    <tr>
                      <th>Role</th>
                      <th>Permissions</th>
                      <th>Status</th>
                      <th>Updated</th>
                      <th className="col-action" style={{ textAlign: "right" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan="5" className="text-center">Loading...</td></tr>
                    ) : rows.length === 0 ? (
                      <tr><td colSpan="5" className="text-center">No roles found</td></tr>
                    ) : rows.map((role) => {
                      const statusUi = getStatusDisplay(role.status);
                      return (
                      <tr key={role.id}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{role.name}</div>
                        </td>
                        <td>{(role.permissions || []).length} assigned</td>
                        <td>
                          <span className={statusUi.badgeClass}>{statusUi.label}</span>
                        </td>
                        <td>{role.updatedAt ? formatDateTime(role.updatedAt) : "—"}</td>
                        <td className="col-action" style={{ textAlign: "right" }}>
                          <div className="support-actions-menu" style={{ position: "relative", display: "inline-block" }}>
                            <button
                              type="button"
                              className="dropdown-trigger-btn btn btn-link p-0"
                              aria-label="Actions"
                              aria-expanded={openActionId === role.id}
                              onClick={(e) => handleOpenDropdown(e, role.id, role)}
                            >
                              <BsThreeDotsVertical />
                            </button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <ListPaginationBar
                page={page}
                setPage={(n) => writeParams(n, filters)}
                total={meta.total || 0}
                pageSize={pageSize}
                totalPages={meta.totalPages || 1}
                ariaLabel="Roles pagination"
                loading={loading}
              />
            </div>
          </div>
        </div>

        {showModal ? (
          <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: "rgba(0,0,0,0.35)" }}>
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{modalTitle}</h5>
                  <button type="button" className="btn-close ms-auto" aria-label="Close" onClick={() => { setShowModal(false); setFormErrors({}); }} />
                </div>
                <div className="modal-body">
                  {modalMode !== "permissions" ? (
                    <div className="mb-3">
                      <label className="form-label mb-1">Role name <span className="text-danger">*</span></label>
                      <input
                        className={`form-control${formErrors.name ? " is-invalid" : ""}`}
                        value={form.name}
                        onChange={(e) => { setForm((p) => ({ ...p, name: e.target.value })); setFormErrors((p) => ({ ...p, name: "" })); }}
                        placeholder="e.g. Support Lead"
                      />
                      {formErrors.name && <div className="invalid-feedback d-block">{formErrors.name}</div>}
                    </div>
                  ) : null}

                  {modalMode === "create" || modalMode === "permissions" ? (
                    <div className="mb-1">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="form-label mb-0">Permissions</label>
                        <div className="d-flex align-items-center gap-3">
                          <button
                            type="button"
                            className="btn btn-sm btn-link p-0"
                            style={{ fontSize: 12, textDecoration: "none" }}
                            onClick={selectAllPermissions}
                          >
                            Select All
                          </button>
                          <span className="text-muted" style={{ fontSize: 12 }}>
                            {form.permission_ids.length} selected
                          </span>
                        </div>
                      </div>
                      {isSupportTeamRole ? (
                        <p className="text-muted mb-2" style={{ fontSize: 12 }}>
                          Customer Support permissions are required for Support Team Member and cannot be changed.
                        </p>
                      ) : null}
                      <div style={{ display: "flex", flexDirection: "column", gap: 16, maxHeight: 420, overflowY: "auto", paddingRight: 4 }}>
                        {permissionsTree.map((section) => (
                          <div key={section.section}>
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 700,
                                letterSpacing: "0.04em",
                                textTransform: "uppercase",
                                color: "#6b7280",
                                marginBottom: 8,
                              }}
                            >
                              {section.section}
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                              {section.menus.map(({ menu, modules }) => {
                                const menuPerms = modules.flatMap((m) => m.list);
                                const menuLocked =
                                  isSupportTeamRole && menu === "Customer Support";
                                const menuAllSelected =
                                  menuPerms.length > 0 &&
                                  menuPerms.every((p) => form.permission_ids.includes(p.id));
                                const open = isMenuOpen(section.section, menu);
                                return (
                                  <div
                                    key={`${section.section}-${menu}`}
                                    style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden", background: "#fff" }}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => toggleMenuOpen(section.section, menu)}
                                      style={{
                                        width: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 12,
                                        padding: "12px 14px",
                                        border: "none",
                                        background: open ? "#f9fafb" : "#fff",
                                        cursor: "pointer",
                                        textAlign: "left",
                                      }}
                                    >
                                      <span style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 600, color: "#111827" }}>
                                        <input
                                          type="checkbox"
                                          checked={menuAllSelected}
                                          disabled={menuLocked}
                                          onClick={(e) => e.stopPropagation()}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            if (menuLocked) return;
                                            toggleModule(menuPerms);
                                          }}
                                        />
                                        {menu}
                                        {menuLocked ? (
                                          <span className="text-muted" style={{ fontSize: 11, fontWeight: 500 }}>
                                            (required)
                                          </span>
                                        ) : null}
                                      </span>
                                      <MdKeyboardArrowDown
                                        size={20}
                                        style={{
                                          color: "#6b7280",
                                          transform: open ? "rotate(180deg)" : "rotate(0deg)",
                                          transition: "transform 0.15s ease",
                                          flexShrink: 0,
                                        }}
                                      />
                                    </button>
                                    <Collapse in={open}>
                                      <div>
                                        <div
                                          style={{
                                            display: "grid",
                                            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                            gap: 12,
                                            padding: "12px",
                                          }}
                                        >
                                          {modules.map(({ module, list }) => {
                                            const moduleLocked =
                                              isSupportTeamRole &&
                                              list.length > 0 &&
                                              list.every((p) => lockedSupportIds.includes(p.id));
                                            const allSelected = list.every((p) => form.permission_ids.includes(p.id));
                                            return (
                                              <div
                                                key={`${section.section}-${menu}-${module}`}
                                                style={{
                                                  border: "1px solid #e5e7eb",
                                                  borderRadius: 10,
                                                  padding: 12,
                                                  background: "#fff",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    gap: 8,
                                                    marginBottom: 10,
                                                    paddingBottom: 8,
                                                    borderBottom: "1px solid #f3f4f6",
                                                  }}
                                                >
                                                  <span style={{ fontWeight: 600, color: "#374151", fontSize: 14 }}>{module}</span>
                                                  <label
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: 6,
                                                      fontSize: 12,
                                                      color: "#6b7280",
                                                      cursor: moduleLocked ? "not-allowed" : "pointer",
                                                      margin: 0,
                                                      opacity: moduleLocked ? 0.7 : 1,
                                                    }}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      checked={allSelected}
                                                      disabled={moduleLocked}
                                                      onChange={() => {
                                                        if (moduleLocked) return;
                                                        toggleModule(list);
                                                      }}
                                                    />
                                                    Select All
                                                  </label>
                                                </div>
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: 8,
                                                  }}
                                                >
                                                  {list.map((p) => {
                                                    const locked = isSupportTeamRole && lockedSupportIds.includes(p.id);
                                                    return (
                                                    <label
                                                      key={p.id}
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 8,
                                                        fontSize: 13,
                                                        color: "#4b5563",
                                                        cursor: locked ? "not-allowed" : "pointer",
                                                        margin: 0,
                                                        opacity: locked ? 0.75 : 1,
                                                      }}
                                                    >
                                                      <input
                                                        type="checkbox"
                                                        checked={form.permission_ids.includes(p.id)}
                                                        disabled={locked}
                                                        onChange={() => togglePermission(p.id)}
                                                      />
                                                      <span style={{ color: "#111827" }}>{p.name}</span>
                                                    </label>
                                                    );
                                                  })}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    </Collapse>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
                <div className="modal-footer">
                  <button type="button" className="add_app_btn btn_theme_primary" onClick={save} disabled={saving}>
                    <span>{saving ? "Saving..." : "Save"}</span>
                  </button>
                  <button type="button" className="add_app_btn_secondary btn_theme_secondary" onClick={() => { setShowModal(false); setFormErrors({}); }}>
                    <span>Close</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {openActionItem && dropdownPosition
        ? createPortal(
          <div
            ref={dropdownRef}
            style={{ position: "fixed", left: dropdownPosition.left, top: dropdownPosition.top, zIndex: 999999, minWidth: 220 }}
          >
            <div className="dropdown-menu-subscription">
              {can("roles.edit") ? (
              <button
                type="button"
                onClick={() => handleDropdownAction(() => openEditName(openActionItem))}
                style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
              >
                <MdEdit size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                <span style={{ fontSize: "0.875rem" }}>Edit</span>
              </button>
              ) : null}
              {can("roles.edit") ? (
              <button
                type="button"
                onClick={() => handleDropdownAction(() => openEditPermissions(openActionItem))}
                style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
              >
                <MdSecurity size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                <span style={{ fontSize: "0.875rem" }}>Change Permissions</span>
              </button>
              ) : null}
              {can("roles.change_status") ? (
              <button
                type="button"
                onClick={() => handleDropdownAction(() => changeStatus(openActionItem, Number(openActionItem.status) === 1 ? 0 : 1))}
                style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
              >
                {Number(openActionItem.status) === 1 ? (
                  <MdOutlineRemoveCircleOutline size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                ) : (
                  <MdOutlineCheckCircle size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                )}
                <span style={{ fontSize: "0.875rem" }}>
                  {Number(openActionItem.status) === 1 ? "Deactivate" : "Activate"}
                </span>
              </button>
              ) : null}
              {!openActionItem.is_system && can("roles.delete") ? (
                <button
                  type="button"
                  className="delete-class"
                  onClick={() => handleDropdownAction(() => handleDelete(openActionItem))}
                  style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
                >
                  <MdDeleteOutline size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                  <span style={{ fontSize: "0.875rem" }}>Delete</span>
                </button>
              ) : null}
            </div>
          </div>,
          document.body
        )
        : null}
    </div>
  );
};

export default Roles;
